import {
  ADMIN_AUDIT_ACTIONS,
  adminAuditApi,
  type AdminAuditAction,
  type AdminAuditLog,
} from '@/api/admin/adminAuditApi';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { usePageResetOnFilterChange } from '@/hooks/usePageResetOnFilterChange';
import { AdminPager } from '@/components/admin/AdminPager';
import { handleGlobalError } from '@/utils/error-handler';
import { ChevronRightIcon } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

const PAGE_SIZE = 20;

const AdminAuditoriaPage = () => {
  const [adminEmail, setAdminEmail] = useState('');
  const [action, setAction] = useState<AdminAuditAction | ''>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(0);
  const [logs, setLogs] = useState<AdminAuditLog[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);

  const debouncedEmail = useDebouncedValue(adminEmail);
  const requestId = useRef(0);

  const load = useCallback(async () => {
    // Two effects can have a fetch in flight at once (a filter change resets the
    // page, and both changes fire a load). Only the newest request may write to
    // state: otherwise a slow stale response overwrites the fresh one.
    const current = ++requestId.current;
    setIsLoading(true);
    try {
      // The input gives a bare date; the endpoint takes an ISO date-time. Widen it to
      // cover the whole local day on each end, or a same-day range would match nothing.
      const fromIso = from ? new Date(`${from}T00:00:00`).toISOString() : undefined;
      const toIso = to ? new Date(`${to}T23:59:59.999`).toISOString() : undefined;
      const result = await adminAuditApi.search({
        adminEmail: debouncedEmail,
        action: action || undefined,
        from: fromIso,
        to: toIso,
        page,
        size: PAGE_SIZE,
      });
      if (current !== requestId.current) return;
      setLogs(result.content);
      setTotalPages(result.totalPages);
    } catch (error) {
      if (current !== requestId.current) return;
      handleGlobalError(
        error,
        'No se pudo cargar la auditoría',
        'Revisá la conexión e intentá de nuevo.',
      );
    } finally {
      if (current === requestId.current) {
        setIsLoading(false);
      }
    }
  }, [debouncedEmail, action, from, to, page]);

  useEffect(() => {
    load();
  }, [load]);

  usePageResetOnFilterChange(`${debouncedEmail}|${action}|${from}|${to}`, page, setPage);

  return (
    <div className="space-y-4 p-6">
      <h1 className="text-2xl font-semibold text-gray-900">Auditoría</h1>

      <div className="flex flex-wrap gap-2">
        <Input
          value={adminEmail}
          onChange={(event) => setAdminEmail(event.target.value)}
          placeholder="Filtrar por email del admin"
          className="max-w-xs"
        />
        <select
          value={action}
          onChange={(event) => setAction(event.target.value as AdminAuditAction | '')}
          className="h-9 rounded-md border border-gray-200 bg-white px-3 text-sm"
        >
          <option value="">Todas las acciones</option>
          {ADMIN_AUDIT_ACTIONS.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
        <input
          type="date"
          value={from}
          onChange={(event) => setFrom(event.target.value)}
          aria-label="Desde"
          className="h-9 rounded-md border border-gray-200 bg-white px-3 text-sm"
        />
        <input
          type="date"
          value={to}
          onChange={(event) => setTo(event.target.value)}
          aria-label="Hasta"
          className="h-9 rounded-md border border-gray-200 bg-white px-3 text-sm"
        />
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, index) => (
            <Skeleton key={index} className="h-12" />
          ))}
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              <TableHead>Fecha</TableHead>
              <TableHead>Admin</TableHead>
              <TableHead>Acción</TableHead>
              <TableHead>Target</TableHead>
              <TableHead>Resultado</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.map((log) => (
              <TableRow
                key={log.id}
                onClick={() => setExpanded(expanded === log.id ? null : log.id)}
                className="cursor-pointer"
              >
                <TableCell className="w-8 p-0 pl-3">
                  <button
                    type="button"
                    aria-expanded={expanded === log.id}
                    aria-label={expanded === log.id ? 'Ocultar detalle' : 'Ver detalle'}
                    // The row is clickable too, as a mouse convenience; without this the row
                    // handler would fire right after and toggle the panel straight back shut.
                    onClick={(event) => {
                      event.stopPropagation();
                      setExpanded(expanded === log.id ? null : log.id);
                    }}
                    className="flex h-6 w-6 items-center justify-center rounded text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                  >
                    <ChevronRightIcon
                      className={`h-4 w-4 transition-transform ${expanded === log.id ? 'rotate-90' : ''}`}
                    />
                  </button>
                </TableCell>
                <TableCell className="whitespace-nowrap text-gray-600">
                  {new Date(log.createdAt).toLocaleString('es-AR')}
                </TableCell>
                <TableCell>{log.adminEmail}</TableCell>
                <TableCell>{log.action}</TableCell>
                <TableCell className="text-gray-600">
                  {log.targetType ? `${log.targetType}: ${log.targetId}` : '—'}
                  {expanded === log.id && log.payload && (
                    <pre className="mt-2 overflow-x-auto rounded bg-gray-50 p-2 text-xs">
                      {log.payload}
                    </pre>
                  )}
                  {expanded === log.id && log.errorMessage && (
                    <p className="mt-2 text-xs text-red-700">{log.errorMessage}</p>
                  )}
                </TableCell>
                <TableCell>
                  <Badge
                    variant="outline"
                    className={
                      log.result === 'OK'
                        ? 'border-green-200 bg-green-100 text-green-700'
                        : 'border-red-200 bg-red-100 text-red-700'
                    }
                  >
                    {log.result}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <AdminPager
        page={page}
        totalPages={totalPages}
        isLoading={isLoading}
        onPageChange={setPage}
      />
    </div>
  );
};

export default AdminAuditoriaPage;
